import { runInAction } from 'mobx';

import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';

import { MetaStore } from '../MetaStore';

type ValueFactoryParams = { title: string };
type ValueFactory = (params: ValueFactoryParams) => string;

type SequentialFieldPrefillStoreParams = {
  title: string;
  field: FieldTextStore;
  valueFactory?: ValueFactory;
};

export class SequentialFieldPrefillStore implements ILocalStore {
  readonly meta = new MetaStore();

  private readonly _title: string;
  private readonly _field: FieldTextStore;
  private readonly _valueFactory?: ValueFactory;

  private _counter = 1;
  private _value: string | null = null;

  constructor({ title, field, valueFactory }: SequentialFieldPrefillStoreParams) {
    this._title = title;
    this._field = field;
    this._valueFactory = valueFactory;
  }

  async ensure(): Promise<void> {
    if (this.meta.isLoading || this.meta.isLoaded) {
      return;
    }

    this.meta.setLoadedStartMeta();
    this._field.setDisabled(true);

    const value = this._resolveValue();

    if (!value) {
      this.meta.setLoadedErrorMeta();
      this._field.setDisabled(false);

      return;
    }

    runInAction(() => {
      this._value = value;
      this._applyValue();
      this.meta.setLoadedSuccessMeta();
      this._field.setDisabled(false);
    });
  }

  reset(): void {
    this.meta.reset();
    this._counter = 1;
    this._value = null;
    this._field.setDisabled(false);
  }

  destroy(): void {
    this.meta.destroy();
    this._field.setDisabled(false);
  }

  private _applyValue() {
    if (this._field.touched || this._field.hasChanged) {
      return;
    }

    const value = this._value ?? this._defaultValue();

    this._field.setValue(value, { noTouch: true });
    this._field.setDefaultValue(value);
    this._field.unTouch();
  }

  private _resolveValue(): string | null {
    if (this._valueFactory) {
      const resolved = this._valueFactory({ title: this._title });

      return resolved;
    }

    return this._defaultValue();
  }

  private _defaultValue(): string {
    const value = `${this._title} ${this._counter}`;

    this._counter += 1;

    return value;
  }
}
